
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Trophy, CheckCircle2, XCircle, RefreshCw, LightbulbIcon, Award, BrainCircuit, Heart, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import SEO from "@/components/SEO";
import { useLocation } from "react-router-dom";

// Define the quiz question type
type QuizQuestion = {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
};

const SeoQuiz = () => {
  const location = useLocation();
  const currentUrl = `https://losangelesseogroup.com${location.pathname}`;
  
  // Quiz questions data
  const quizQuestions: QuizQuestion[] = [
    {
      id: 1,
      question: "What does SEO stand for?",
      options: [
        "Search Engine Organization",
        "Search Engine Optimization",
        "Search Engine Operations",
        "Site Engine Optimization"
      ],
      correctAnswer: 1,
      explanation: "SEO stands for Search Engine Optimization, which is the practice of increasing the quantity and quality of traffic to your website through organic search engine results."
    },
    {
      id: 2,
      question: "Which of these is NOT a ranking factor for SEO?",
      options: [
        "Page loading speed",
        "Mobile-friendliness",
        "The number of images on a page",
        "Quality of content"
      ],
      correctAnswer: 2,
      explanation: "While images can enhance user experience, the mere number of images is not a direct ranking factor. Page speed, mobile usability, and content quality are all important ranking factors."
    },
    {
      id: 3,
      question: "What is a backlink?",
      options: [
        "A link that takes you back to the homepage",
        "A broken link that needs fixing",
        "A link from another website to your website",
        "A link to a previous version of your page"
      ],
      correctAnswer: 2,
      explanation: "A backlink is a link created when one website links to another. Backlinks are also called 'inbound links' and are important for SEO as they represent a 'vote of confidence' from one site to another."
    },
    {
      id: 4,
      question: "What is keyword stuffing?",
      options: [
        "A technique to highlight keywords on your page",
        "The practice of overusing keywords on a page",
        "A method to organize keywords in your content",
        "A tool to research the best keywords"
      ],
      correctAnswer: 1,
      explanation: "Keyword stuffing is the practice of loading a webpage with keywords in an attempt to manipulate a site's ranking in search results. This practice is against Google's guidelines and can result in penalties."
    },
    {
      id: 5,
      question: "What is the meta description used for?",
      options: [
        "It's the main content of your page",
        "It provides a brief summary of a page for search engines and users",
        "It's where you list all your keywords",
        "It's used to create page titles"
      ],
      correctAnswer: 1,
      explanation: "The meta description is an HTML attribute that provides a brief summary of a web page. Search engines often display the meta description in search results, which can influence click-through rates."
    },
    {
      id: 6,
      question: "What is local SEO?",
      options: [
        "SEO that only works in your local network",
        "SEO focused on improving visibility for local searches",
        "SEO that works only with local businesses",
        "A simplified version of SEO"
      ],
      correctAnswer: 1,
      explanation: "Local SEO is a strategy that helps your business be more visible in local search results. It's particularly important for businesses that have physical locations or serve specific geographic areas."
    },
    {
      id: 7,
      question: "What is a featured snippet?",
      options: [
        "A paid advertisement on search results",
        "A piece of code that enhances your website",
        "A selected search result that appears in a box at the top of Google's results",
        "A method to highlight text on your website"
      ],
      correctAnswer: 2,
      explanation: "A featured snippet is a selected search result that appears in a box at the top of Google's search results. It's designed to answer the user's question right away, without requiring a click through to a website."
    },
    {
      id: 8,
      question: "What is domain authority?",
      options: [
        "The legal right to own a domain name",
        "A search engine ranking score that predicts how well a website will rank",
        "The age of your domain name",
        "The authority you have to manage a domain"
      ],
      correctAnswer: 1,
      explanation: "Domain Authority (DA) is a search engine ranking score developed by Moz that predicts how likely a website is to rank in search engine result pages. It ranges from 1 to 100, with higher scores indicating a greater ability to rank."
    },
    {
      id: 9,
      question: "What is a canonical tag used for?",
      options: [
        "To mark a page as the original version when duplicate content exists",
        "To indicate the category of your website",
        "To label images on your website",
        "To specify the language of your content"
      ],
      correctAnswer: 0,
      explanation: "A canonical tag (rel='canonical') is a way of telling search engines that a specific URL represents the master copy of a page. It's used to prevent problems caused by identical or 'duplicate' content appearing on multiple URLs."
    },
    {
      id: 10,
      question: "What is a 301 redirect?",
      options: [
        "A type of server error",
        "A permanent redirect from one URL to another",
        "A temporary redirect for website maintenance",
        "A code that blocks search engines from indexing a page"
      ],
      correctAnswer: 1,
      explanation: "A 301 redirect is a permanent redirect from one URL to another. It's the most efficient and search engine friendly method for webpage redirection, and it passes between 90-99% of ranking power to the redirected page."
    }
  ];

  // State variables
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [answers, setAnswers] = useState<number[]>([]);

  // Handle answer selection
  const handleAnswerSelect = (answerIndex: number) => {
    if (selectedAnswerIndex === null) {
      setSelectedAnswerIndex(answerIndex);
      const isCorrect = answerIndex === quizQuestions[currentQuestionIndex].correctAnswer;
      if (isCorrect) {
        setScore(score + 1);
      }
      
      // Save this answer
      const newAnswers = [...answers];
      newAnswers[currentQuestionIndex] = answerIndex;
      setAnswers(newAnswers);
      
      // Show explanation after selection
      setShowExplanation(true);
    }
  };

  // Move to the next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswerIndex(null);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
    }
  };

  // Restart the quiz
  const restartQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswerIndex(null);
    setScore(0);
    setShowExplanation(false);
    setQuizCompleted(false);
    setAnswers([]);
  };

  // Get appropriate icon and style for result
  const getResultIcon = (score: number) => {
    const percentage = (score / quizQuestions.length) * 100;
    
    if (percentage >= 80) {
      return <Trophy className="h-16 w-16 text-yellow-500" />;
    } else if (percentage >= 60) {
      return <Award className="h-16 w-16 text-blue-500" />;
    } else {
      return <LightbulbIcon className="h-16 w-16 text-purple-500" />;
    }
  };

  // Get result message based on score
  const getResultMessage = (score: number) => {
    const percentage = (score / quizQuestions.length) * 100;
    
    if (percentage >= 80) {
      return "SEO Expert! You really know your stuff!";
    } else if (percentage >= 60) {
      return "SEO Enthusiast! You have a good foundation of knowledge.";
    } else {
      return "SEO Beginner! There's room to grow your knowledge.";
    }
  };

  // Calculate progress percentage
  const progressPercentage = ((currentQuestionIndex + 1) / quizQuestions.length) * 100;

  return (
    <>
      <SEO 
        seo={{
          title: "SEO Quiz | Test Your SEO Knowledge",
          description: "Take our fun SEO quiz to test your knowledge about search engine optimization and learn interesting facts."
        }}
        url={currentUrl}
      />
      <Navbar />
      <PageTransition>
        <main className="page-container section-padding py-12 min-h-[80vh]">
          <div className="max-w-3xl mx-auto">
            {!quizCompleted ? (
              <>
                <div className="mb-8">
                  <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                    Test Your SEO Knowledge
                  </h1>
                  <p className="text-muted-foreground text-center mb-6">
                    Answer these 10 questions to see how much you know about Search Engine Optimization.
                  </p>
                  <div className="mb-4">
                    <Progress value={progressPercentage} className="h-2" />
                    <p className="text-sm text-muted-foreground mt-2">
                      Question {currentQuestionIndex + 1} of {quizQuestions.length}
                    </p>
                  </div>
                </div>

                <div className="bg-card border rounded-lg shadow-sm p-6 mb-8">
                  <h2 className="text-xl font-semibold mb-4">
                    {quizQuestions[currentQuestionIndex].question}
                  </h2>
                  
                  <div className="space-y-3">
                    {quizQuestions[currentQuestionIndex].options.map((option, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <button
                          className={`w-full text-left p-4 rounded-md border transition-colors flex items-center space-x-3 ${
                            selectedAnswerIndex === index 
                              ? index === quizQuestions[currentQuestionIndex].correctAnswer
                                ? "bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-800"
                                : "bg-red-100 border-red-300 dark:bg-red-900/30 dark:border-red-800"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => handleAnswerSelect(index)}
                          disabled={selectedAnswerIndex !== null}
                        >
                          {selectedAnswerIndex === index ? (
                            index === quizQuestions[currentQuestionIndex].correctAnswer ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                            )
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 flex-shrink-0"></div>
                          )}
                          <span>{option}</span>
                        </button>
                      </motion.div>
                    ))}
                  </div>

                  {showExplanation && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-6 p-4 bg-muted/50 rounded-md"
                    >
                      <h3 className="font-medium mb-2">Explanation:</h3>
                      <p>{quizQuestions[currentQuestionIndex].explanation}</p>
                    </motion.div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleNextQuestion}
                    disabled={selectedAnswerIndex === null}
                    size="lg"
                  >
                    {currentQuestionIndex < quizQuestions.length - 1 ? "Next Question" : "See Results"}
                  </Button>
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                <div className="mb-8">
                  <div className="flex justify-center mb-4">
                    {getResultIcon(score)}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">
                    {getResultMessage(score)}
                  </h1>
                  <p className="text-xl">
                    You scored <span className="font-bold">{score}</span> out of {quizQuestions.length}
                  </p>
                </div>

                <div className="bg-card border rounded-lg shadow-sm p-6 mb-8">
                  <h2 className="text-xl font-semibold mb-4">Your Answers</h2>
                  <div className="space-y-4">
                    {quizQuestions.map((question, index) => (
                      <div key={index} className="border-b pb-4 last:border-b-0 text-left">
                        <div className="flex items-start gap-3">
                          {answers[index] === question.correctAnswer ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-1 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-1 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium">{question.question}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Your answer: <span className={answers[index] === question.correctAnswer ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                                {question.options[answers[index]]}
                              </span>
                            </p>
                            {answers[index] !== question.correctAnswer && (
                              <p className="text-sm text-muted-foreground">
                                Correct answer: <span className="text-green-600 dark:text-green-400">
                                  {question.options[question.correctAnswer]}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button 
                    onClick={restartQuiz}
                    variant="outline"
                    size="lg"
                    className="flex gap-2 items-center"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    className="flex gap-2 items-center"
                  >
                    <a href="/knowledge-base">
                      <BrainCircuit className="h-4 w-4" />
                      Learn More About SEO
                    </a>
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </PageTransition>
      <Footer />
    </>
  );
};

export default SeoQuiz;
